import type {
  BatchLookupResult,
  ClientForBatch,
  EffectiveScreeningStatus,
} from "@repo/shared/types";
import { AlertCircle, Pencil, ShieldAlert } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { get } from "@/lib/api/_client";
import { useClientTabs, useEditFields } from "@/lib/hooks/use-clients";
import { useBatchLookup, useClientsForView, useComplianceViews } from "@/lib/hooks/use-compliance";
import { useTrapetsMode } from "@/lib/hooks/use-trapets";
import { useT } from "@/lib/i18n/use-t";
import type { ClientWithCustomValues } from "@/lib/types/client";
import { ClientDialog } from "@/pages/clients/client-dialog";
import { T } from "./batch-lookup-wrapper.i18n";

/** Same status → variant mapping the Clients review list uses. */
const STATUS_VARIANT: Record<
  EffectiveScreeningStatus,
  "secondary" | "success" | "warning" | "destructive"
> = {
  not_checked: "secondary",
  failed: "secondary",
  clear: "success",
  // Amber: the sanction half passed, the PEP half was never asked.
  incomplete: "warning",
  needs_review: "warning",
  accepted: "success",
  blocked: "destructive",
  // Green like `clear` — a compliance user ruled the listing out. The label
  // keeps the distinction that a human decided it.
  not_a_match: "success",
};

type Stage = "select-view" | "review-clients" | "results";

export function BatchLookupWrapper() {
  const t = useT(T);
  const statusLabel = (s: EffectiveScreeningStatus) =>
    ({
      not_checked: t.stNotChecked,
      failed: t.stFailed,
      clear: t.stClear,
      incomplete: t.stIncomplete,
      needs_review: t.stNeedsReview,
      accepted: t.stAccepted,
      blocked: t.stBlocked,
      not_a_match: t.stNotAMatch,
    })[s] ?? s;

  const { trapetsMode } = useTrapetsMode();
  const { data: views = [], isLoading: viewsLoading } = useComplianceViews();
  const { editFields } = useEditFields();
  const { clientTabs } = useClientTabs();

  const clientsForViewMutation = useClientsForView();
  const batchLookupMutation = useBatchLookup();

  const [stage, setStage] = useState<Stage>("select-view");
  const [selectedViewId, setSelectedViewId] = useState<string>("");
  const [clients, setClients] = useState<ClientForBatch[]>([]);
  const [results, setResults] = useState<BatchLookupResult[]>([]);
  const [isTestMode, setIsTestMode] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<ClientWithCustomValues | null>(null);

  const isPending = clientsForViewMutation.isPending || batchLookupMutation.isPending;

  const handleViewSelect = (viewId: string) => {
    setSelectedViewId(viewId);
    setError(null);
  };

  const handleLoadClients = () => {
    if (!selectedViewId) return;

    clientsForViewMutation.mutate(selectedViewId, {
      onSuccess: (data) => {
        setClients(data);
        setStage("review-clients");
        setError(null);
      },
      onError: (err) => {
        setError(err.message || t.failedToLoadClients);
      },
    });
  };

  const handleRunLookup = () => {
    const validClients = clients.filter((c) => c.isValid);
    if (validClients.length === 0) {
      setError(t.noValidClients);
      return;
    }

    batchLookupMutation.mutate(clients, {
      onSuccess: (data) => {
        setResults(data.results);
        setIsTestMode(data.isTestMode);
        setStage("results");
        setError(null);
      },
      onError: (err) => {
        setError(err.message || t.failedToRunBatchLookup);
      },
    });
  };

  const handleReset = () => {
    setStage("select-view");
    setSelectedViewId("");
    setClients([]);
    setResults([]);
    setError(null);
  };

  const handleEditClient = async (clientForBatch: ClientForBatch) => {
    try {
      const fullClient = await get<ClientWithCustomValues>(
        `/internal/clients/${clientForBatch.id}`,
      );
      setEditingClient(fullClient);
      setEditDialogOpen(true);
    } catch {
      // ignore
    }
  };

  const handleDialogClose = async () => {
    setEditDialogOpen(false);
    setEditingClient(null);
    // Refresh the client list to get updated validation status
    if (selectedViewId) {
      clientsForViewMutation.mutate(selectedViewId, {
        onSuccess: (data) => setClients(data),
      });
    }
  };

  const validCount = clients.filter((c) => c.isValid).length;
  const invalidCount = clients.filter((c) => !c.isValid).length;
  const hitsCount = results.filter((r) => r.hasHits).length;

  // Trapets disabled for the tenant → explicit disabled state instead of letting a
  // batch run reach the API and surface a generic request failure.
  if (trapetsMode === "disabled") {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-muted-foreground" />
            {t.trapetsDisabledTitle}
          </CardTitle>
          <CardDescription>{t.trapetsDisabled}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Progress indicator */}
      <div className="flex items-center gap-4">
        <div
          className={`flex items-center gap-2 ${stage === "select-view" ? "text-primary font-medium" : "text-muted-foreground"}`}
        >
          <div
            className={`w-6 h-6 rounded-full flex items-center justify-center text-sm ${stage === "select-view" ? "bg-primary text-primary-foreground" : "bg-muted"}`}
          >
            1
          </div>
          <span>{t.selectView}</span>
        </div>
        <div className="h-px w-8 bg-border" />
        <div
          className={`flex items-center gap-2 ${stage === "review-clients" ? "text-primary font-medium" : "text-muted-foreground"}`}
        >
          <div
            className={`w-6 h-6 rounded-full flex items-center justify-center text-sm ${stage === "review-clients" ? "bg-primary text-primary-foreground" : "bg-muted"}`}
          >
            2
          </div>
          <span>{t.reviewClients}</span>
        </div>
        <div className="h-px w-8 bg-border" />
        <div
          className={`flex items-center gap-2 ${stage === "results" ? "text-primary font-medium" : "text-muted-foreground"}`}
        >
          <div
            className={`w-6 h-6 rounded-full flex items-center justify-center text-sm ${stage === "results" ? "bg-primary text-primary-foreground" : "bg-muted"}`}
          >
            3
          </div>
          <span>{t.results}</span>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 rounded-md bg-destructive/10 text-destructive">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Stage 1: Select View */}
      {stage === "select-view" && (
        <Card>
          <CardHeader>
            <CardTitle>{t.selectClientView}</CardTitle>
            <CardDescription>{t.selectClientViewDesc}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Select value={selectedViewId} onValueChange={handleViewSelect}>
              <SelectTrigger className="w-full max-w-md">
                <SelectValue placeholder={t.selectViewPlaceholder} />
              </SelectTrigger>
              <SelectContent>
                {views.map((view) => (
                  <SelectItem key={view.id} value={view.id}>
                    {view.name}
                    {view.isStandard && (
                      <span className="ml-2 text-xs text-muted-foreground">{t.standard}</span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              onClick={handleLoadClients}
              disabled={!selectedViewId || viewsLoading}
              loading={isPending}
            >
              {t.loadClients}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Stage 2: Review Clients */}
      {stage === "review-clients" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{t.reviewClients}</span>
              <div className="flex items-center gap-2">
                <Badge variant="secondary">
                  {clients.length} {t.total}
                </Badge>
                <Badge variant="default">
                  {validCount} {t.valid}
                </Badge>
                {invalidCount > 0 && (
                  <Badge variant="destructive">
                    {invalidCount} {t.invalid}
                  </Badge>
                )}
              </div>
            </CardTitle>
            <CardDescription>{t.reviewClientsDesc}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {clients.length === 0 ? (
              <p className="text-muted-foreground">{t.noClientsInView}</p>
            ) : (
              <div className="max-h-[400px] overflow-auto">
                <Table variant="data" surface={false}>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t.currentStatus}</TableHead>
                      <TableHead>{t.name}</TableHead>
                      <TableHead>{t.type}</TableHead>
                      <TableHead>{t.ssnOrgNumber}</TableHead>
                      <TableHead>{t.country}</TableHead>
                      <TableHead>{t.issues}</TableHead>
                      <TableHead className="w-[60px]">{t.edit}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clients.map((client) => (
                      <TableRow
                        key={client.id}
                        className={!client.isValid ? "bg-destructive/5" : undefined}
                      >
                        {/* QL-21 — the CURRENT compliance standing, not a
                            validity tick. Whether a row can be screened is
                            already said three times over: the Issues cell, the
                            row highlight and the Valid/Invalid counts. The word
                            "status" belongs to the thing a compliance user
                            actually tracks. */}
                        <TableCell>
                          <Badge variant={STATUS_VARIANT[client.status]}>
                            {statusLabel(client.status)}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">{client.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{client.type}</Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {client.type === "Person" ? client.ssn || "-" : client.orgId || "-"}
                        </TableCell>
                        <TableCell>{client.country || "-"}</TableCell>
                        <TableCell>
                          {client.validationErrors.length > 0 && (
                            <span className="text-sm text-destructive">
                              {client.validationErrors.join(", ")}
                            </span>
                          )}
                          {/* A gap, not a blocker: the run still goes ahead and
                              screens the entity, but the PEP half will come back
                              unassessed. Surfaced HERE so the fix (register a
                              related party) is obvious before the run rather
                              than after it. */}
                          {client.validationErrors.length === 0 && client.pepGap && (
                            <span className="text-sm text-warning">{client.pepGap}</span>
                          )}
                          {client.representativeName && (
                            <span className="text-xs text-muted-foreground">
                              {t.pepVia} {client.representativeName}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditClient(client)}
                            disabled={isPending}
                            tooltip={t.edit}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            <div className="flex items-center gap-2 pt-4">
              <Button variant="outline" onClick={handleReset}>
                {t.back}
              </Button>
              <Button onClick={handleRunLookup} disabled={validCount === 0} loading={isPending}>
                {t.runBatchLookup} ({validCount} {t.clients})
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stage 3: Results */}
      {stage === "results" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span>{t.batchLookupResults}</span>
                {isTestMode && <Badge variant="warning">{t.test}</Badge>}
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary">
                  {results.length} {t.searched}
                </Badge>
                {hitsCount > 0 ? (
                  <Badge variant="destructive">
                    {hitsCount} {t.hits}
                  </Badge>
                ) : (
                  <Badge variant="default">{t.noHits}</Badge>
                )}
              </div>
            </CardTitle>
            <CardDescription>
              {hitsCount > 0 ? t.foundClientsWithMatches(String(hitsCount)) : t.noMatchesFound}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="max-h-[500px] overflow-auto">
              <Table variant="data" surface={false}>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t.updatedStatus}</TableHead>
                    <TableHead>{t.name}</TableHead>
                    <TableHead>{t.type}</TableHead>
                    <TableHead>{t.pep}</TableHead>
                    <TableHead>{t.sanctioned}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {results.map((result) => (
                    <TableRow
                      key={result.clientId}
                      className={result.hasHits ? "bg-destructive/5" : undefined}
                    >
                      {/* The client's standing AFTER this run — a shield icon
                          said nothing a reader could act on, and repeated what
                          the PEP/Sanction columns already show. */}
                      <TableCell>
                        {result.error ? (
                          <span className="text-sm text-destructive">{result.error}</span>
                        ) : (
                          <Badge variant={STATUS_VARIANT[result.status]}>
                            {statusLabel(result.status)}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">
                        <Link
                          to={`/clients/${result.clientId}`}
                          className="hover:underline text-primary"
                        >
                          {result.clientName}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{result.clientType}</Badge>
                      </TableCell>
                      {/* QL-21 — the two independent findings, same colours as
                          the Clients review list and the flow's own lookup
                          widget. The old hit-count and "review required" columns
                          are gone: follow-up happens in Clients review, and a
                          count of matched rows was never the thing to act on. */}
                      <TableCell>
                        {/* PEP is tri-state. null means nobody was asked — a
                            Company/Group with no registered representative —
                            and shows amber, not a neutral dash: the dash keyed
                            off `clientType === "Company"` hid the gap even when
                            a representative HAD been screened and found. */}
                        {result.isPep === null ? (
                          <Badge variant="warning">{t.pepNotAssessed}</Badge>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Badge variant={result.isPep ? "warning" : "success"}>
                              {result.isPep ? t.yes : t.no}
                            </Badge>
                            {result.subject?.kind === "representative" && (
                              <span className="text-xs text-muted-foreground">
                                {result.subject.name}
                              </span>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={result.isSanctioned ? "destructive" : "success"}>
                          {result.isSanctioned ? t.yes : t.no}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="flex items-center justify-between pt-4">
              <Button variant="outline" onClick={handleReset}>
                {t.startOver}
              </Button>
              {/* The run is only half the job — what matters next is working
                  through what it turned up, which is Clients review filtered to
                  the ones nobody has signed off. */}
              <Button asChild>
                <Link to="/compliance/clients?screening=needs_review">{t.goToReview}</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <ClientDialog
        open={editDialogOpen}
        client={editingClient}
        customFields={editFields}
        clientTabs={clientTabs}
        onClose={handleDialogClose}
      />
    </div>
  );
}
